<template>
  <div
    :class="{
      'el-textarea__inner': !viewOnly,
      'bc-note-editor': true,
      'u-p1': true
    }"
    @text-truncated="$emit('text-truncated')"
  >
    <note-editor-menu-bubble
      v-test-id="'note-editor-menu-bubble'"
      v-if="shouldShowMenuBar"
      :editor="editor"
      :options="menuBubbleOptions"
    />
    <note-editor-menu-bar
      v-test-id="'note-editor-menu-bar'"
      v-if="shouldShowMenuBar"
      :editor="editor"
      :options="menuBarOptions"
      :visible-options="el.is.size"
      class="u-pl2 u-mb2"
    />
    <div
      data-test-id="note-editor-content"
      @click="onEditorClick"
    >
      <editor-content
        v-bc-truncate-text="truncateConfig"
        :style="styles"
        :editor="editor"
        class="bc-note-editor__content"
        :class="{'bc-note-editor__content--active': editable}"
        v-data-element:textarea="{ dataElement: dataElement || 'content' }"
      />
    </div>
  </div>
</template>

<style lang="scss" src="./_note-editor.scss"></style>

<script>
import {Editor, EditorContent} from 'tiptap'
import {
  Blockquote,
  Bold,
  BulletList,
  HardBreak,
  History,
  HorizontalRule,
  Italic,
  Link,
  ListItem,
  OrderedList,
  Placeholder,
  Strike,
  Underline
} from 'tiptap-extensions'
import {
  AlignText,
  Indentation
} from '#/notes/tiptap-extensions'
import {ResponsiveMixin} from 'vue-responsive-components'
import {getRemToPx} from '#/notes/utilities/window'
import BCTruncateText from '#/notes/components/directives/BCTruncateText'
import NoteEditorMenuBar from '#/notes/components/NoteEditorMenuBar'
import NoteEditorMenuBubble from '#/notes/components/NoteEditorMenuBubble'

export default {
  name: 'NoteEditor',
  components: {
    EditorContent,
    NoteEditorMenuBar,
    NoteEditorMenuBubble
  },
  mixins: [ResponsiveMixin],
  breakpoints: {
    size: el => {
      // BC-Design-System defines a 10px gap between buttons
      // Ref: packages/theme-chalk/src/button.scss
      return Math.floor(el.width / (getRemToPx() + 10))
    }
  },
  directives: {
    'bc-truncate-text': BCTruncateText
  },
  props: {
    editable: {
      type: Boolean,
      default: true
    },
    shouldShowMenuBar: {
      type: Boolean,
      default: true
    },
    value: {
      type: String
    },
    styles: {
      type: Object,
      default: null
    },
    truncateContent: {
      type: Boolean,
      default: false
    },
    extensions: {
      type: Array,
      default: () => [
        new AlignText(),
        new Blockquote(),
        new Bold(),
        new BulletList(),
        new HardBreak(),
        new History(),
        new HorizontalRule(),
        new Indentation(),
        new Italic(),
        new Link(),
        new ListItem(),
        new OrderedList(),
        new Placeholder({
          emptyNodeText: 'Write something …',
          showOnlyWhenEditable: true,
          showOnlyCurrent: true
        }),
        new Strike(),
        new Underline()
      ]
    },
    dataElement: String
  },
  data () {
    return {
      editor: new Editor({
        // Editor only accepts Array, and not reactive object.
        extensions: this.extensions.splice(0),
        editable: this.editable,
        onInit: this.onEditorInit,
        onUpdate: this.onEditorUpdate
      }),
      contentActive: false,
      isEmpty: true,
      menuBarOptions: [{
        name: 'undo',
        label: 'Undo (Mod-Z)',
        icon: ['far', 'undo-alt'],
        inactive: true
      }, {
        name: 'redo',
        label: 'Redo (Mod-Shft-Z)',
        icon: ['far', 'redo-alt'],
        inactive: true
      }, {
        name: 'bold',
        label: 'Bold (Mod-b)',
        icon: 'bold'
      }, {
        name: 'underline',
        label: 'Underline (Mod-u)',
        icon: 'underline'
      }, {
        name: 'italic',
        label: 'Italic (Mod-i)',
        icon: 'italic'
      }, {
        name: 'link',
        label: 'URL',
        icon: ['far', 'link']
      }, {
        name: 'strike',
        label: 'Strikethrough',
        icon: 'strikethrough'
      }, {
        name: 'bullet_list',
        label: 'Bullet List',
        icon: ['far', 'list']
      }, {
        name: 'ordered_list',
        label: 'Numbered List',
        icon: ['far', 'list-ol']
      }, {
        name: 'horizontal_rule',
        label: 'Horizontal Rule',
        icon: ['far', 'horizontal-rule']
      }, {
        name: 'indentation',
        label: 'Indent (Tab)',
        icon: ['far', 'indent'],
        attrs: {indent: true},
        inactive: true
      }, {
        name: 'indentation',
        label: 'Outdent (Shift-Tab)',
        icon: ['far', 'outdent'],
        attrs: {indent: false},
        inactive: true
      }, {
        name: 'align_text',
        label: 'Justify Text',
        icon: ['far', 'align-justify'],
        attrs: {align: 'justify'}
      }, {
        name: 'align_text',
        label: 'Align Text to Left',
        icon: ['far', 'align-left'],
        attrs: {align: 'left'}
      }, {
        name: 'align_text',
        label: 'Center Text',
        icon: ['far', 'align-center'],
        attrs: {align: 'center'}
      }, {
        name: 'align_text',
        label: 'Align Text to Right',
        icon: ['far', 'align-right'],
        attrs: {align: 'right'}
      }],
      menuBubbleOptions: [{
        name: 'bold',
        label: 'Bold (Mod-b)',
        icon: 'bold'
      }, {
        name: 'underline',
        label: 'Underline (Mod-u)',
        icon: 'underline'
      }, {
        name: 'italic',
        label: 'Italic (Mod-i)',
        icon: 'italic'
      }, {
        name: 'link',
        label: 'URL',
        icon: ['far', 'link']
      }, {
        name: 'strike',
        label: 'Strikethrough',
        icon: 'strikethrough'
      }, {
        name: 'bullet_list',
        label: 'Bullet List',
        icon: ['far', 'list']
      }, {
        name: 'ordered_list',
        label: 'Numbered List',
        icon: ['far', 'list-ol']
      }]
    }
  },
  computed: {
    shouldTruncate () {
      return this.contentActive && this.truncateContent
    },
    truncateConfig () {
      return {
        maxHeight: 140,
        isActive: this.shouldTruncate
      }
    },
    viewOnly () {
      return !this.editable && !this.shouldShowMenuBar
    }
  },
  watch: {
    'value': {
      handler (contentValue) {
        // During editing state, the note editor content is not empty.
        if (this.editable && !this.isEmpty) {
          // When content is set to empty via prop, we should empty the note editor content
          if (contentValue === '') {
            this.editor.clearContent()
            this.isEmpty = true
          }
          // As the user is editing the note editor content, short-circuit the remaining logic
          return
        }

        // During initial state, the note editor content is empty.
        if (this.isEmpty) {
          // When content is set via prop, we should set it as the note editor content
          if (contentValue) {
            this.editor.setContent(contentValue, true)
          }
          // During initial state, set the note editor options
          this.editor.setOptions({
            editable: this.editable
          })
        }
      },
      immediate: true
    }
  },
  beforeDestroy () {
    this.editor.destroy()
  },
  methods: {
    onEditorClick () {
      // Focus only if it is not focused at this moment
      if (!this.editor.view.focused) {
        this.editor.focus('end')
      }
    },
    onEditorInit () {
      this.$nextTick(() => {
        this.contentActive = true
        this.$emit('init', {editor: this.editor})
      })
    },
    onEditorUpdate ({getHTML}) {
      const content = getHTML()
      this.isEmpty = content === '<p></p>'
      this.$emit('input', this.isEmpty ? '' : content)
    }
  }
}
</script>
